// Point-in-polygon utility using ray-casting algorithm

type Ring = number[][];

/**
 * Check if a point is inside a polygon ring using the ray-casting algorithm.
 * @param lat - Latitude of the point
 * @param lng - Longitude of the point
 * @param ring - Array of [lng, lat] coordinate pairs (GeoJSON order)
 * @returns true if the point is inside the ring
 */
function pointInRing(lat: number, lng: number, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0],
      yi = ring[i][1];
    const xj = ring[j][0],
      yj = ring[j][1];

    const intersect = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Check if a point (lat, lng) is inside a GeoJSON Polygon or MultiPolygon geometry.
 * @param lat - Latitude
 * @param lng - Longitude
 * @param geometry - GeoJSON geometry (Polygon or MultiPolygon)
 * @returns true if the point is inside the geometry
 */
export function pointInPolygon(lat: number, lng: number, geometry: GeoJSON.Geometry): boolean {
  if (geometry.type === 'Polygon') {
    const coords = geometry.coordinates as number[][][];
    // Check outer ring
    if (!pointInRing(lat, lng, coords[0])) return false;
    // Check holes (if any): point must NOT be in any hole
    for (let i = 1; i < coords.length; i++) {
      if (pointInRing(lat, lng, coords[i])) return false;
    }
    return true;
  }

  if (geometry.type === 'MultiPolygon') {
    const polygons = geometry.coordinates as number[][][][];
    for (const polygon of polygons) {
      if (!pointInRing(lat, lng, polygon[0])) continue;
      let inHole = false;
      for (let i = 1; i < polygon.length; i++) {
        if (pointInRing(lat, lng, polygon[i])) {
          inHole = true;
          break;
        }
      }
      if (!inHole) return true;
    }
    return false;
  }

  return false;
}
